/**
 * MobileTalkOverlay Component
 * 
 * Slide-up overlay for transcript and voice interaction.
 * Reuses TranscriptInput from desktop for consistent UX.
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { TranscriptInput, type TranscriptMessage } from '@/components/chat/TranscriptPanel';
import StudibudiFace from '@/components/brand/StudibudiFace';
import MobileWelcome from './MobileWelcome';
import './mobile.css';

interface MobileTalkOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    messages: TranscriptMessage[];
    onSendText?: (text: string) => void;
    agentStatus: 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'ending' | 'error';
    isUserSpeaking?: boolean;
    isAgentSpeaking?: boolean;
    // Dock controls
    isUserMuted?: boolean;
    onToggleMute?: () => void;
    onDisconnect?: () => void;
    onConnect?: () => void;
}

const MobileTalkOverlay: React.FC<MobileTalkOverlayProps> = ({
    isOpen,
    onClose,
    messages,
    onSendText,
    agentStatus,
    isUserSpeaking = false,
    isAgentSpeaking = false,
    isUserMuted = false,
    onToggleMute,
    onDisconnect,
    onConnect,
}) => {
    const transcriptRef = useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [isOpening, setIsOpening] = useState(false);

    const isConnected = agentStatus === 'connected';
    const isConnecting = agentStatus === 'connecting' || agentStatus === 'authenticating';

    // Handle opening animation - delay applying .open class to allow initial state to render
    useEffect(() => {
        if (isOpen && !isClosing) {
            const timer = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsOpening(true);
                });
            });
            return () => cancelAnimationFrame(timer);
        } else {
            setIsOpening(false);
        }
    }, [isOpen, isClosing]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (transcriptRef.current) {
            transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
        }
    }, [messages]);

    // Handle close with animation
    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 250);
    }, [onClose]);

    // Handle send
    const handleSend = useCallback(() => {
        if (inputValue.trim() && onSendText) {
            onSendText(inputValue.trim());
            setInputValue('');
        }
    }, [inputValue, onSendText]);

    // Status text
    const getStatusText = () => {
        if (agentStatus === 'disconnected') return 'Disconnected';
        if (isConnecting) return 'Connecting...';
        if (isUserSpeaking) return "You're speaking";
        if (isAgentSpeaking) return 'StudiBudi is speaking';
        return 'Listening...';
    };

    // Status indicator color
    const getStatusColor = () => {
        if (isConnected) {
            if (isUserSpeaking) return 'user-speaking';
            if (isAgentSpeaking) return 'agent-speaking';
            return 'listening';
        }
        return agentStatus;
    };

    if (!isOpen && !isClosing) return null;

    return (
        <>
            {/* Overlay backdrop */}
            <div
                className={`mobile-talk-backdrop ${isOpening && !isClosing ? 'open' : ''}`}
                onClick={handleClose}
            />

            {/* Overlay panel */}
            <div className={`mobile-talk-overlay ${isOpening && !isClosing ? 'open' : ''}`}>
                {/* Header with agent info - matches desktop TranscriptHeader */}
                <div className="px-5 pt-4 pb-3">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                            <StudibudiFace className="h-8 w-6" isActive={isConnected} />
                            <div className="flex flex-col">
                                <p className="text-[15px] font-semibold text-[#37352f] leading-snug">Studibudi</p>
                                <p className={`text-[13px] leading-snug flex items-center gap-1.5 ${isConnected ? 'text-emerald-600' : 'text-[#787774]'}`}>
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                    {isConnected ? 'Connected' : 'Disconnected'}
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleClose}
                            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors hover:text-slate-600"
                            aria-label="Close panel"
                        >
                            <ChevronDown className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Transcript messages */}
                <div className="mobile-talk-transcript" ref={transcriptRef}>
                    {messages.length === 0 ? (
                        <MobileWelcome 
                            variant="overlay"
                            onTapToStart={onConnect}
                            isOnboardingMode={false}
                            onPromptSelect={(promptText) => {
                                // Send the prompt text directly
                                if (onSendText) {
                                    onSendText(promptText);
                                }
                            }}
                        />
                    ) : (
                        messages.map((msg) => {
                            if (msg.type === 'agent') {
                                return (
                                    <div key={msg.id} className="mobile-talk-message agent">
                                        {msg.content}
                                    </div>
                                );
                            }
                            return (
                                <div key={msg.id} className="flex flex-col items-end gap-1">
                                    <div className="mobile-talk-message user">
                                        {msg.content}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Input area - reuses TranscriptInput from desktop */}
                <div className="mobile-talk-input-container">
                    <TranscriptInput
                        value={inputValue}
                        setValue={setInputValue}
                        onSend={handleSend}
                        isConnected={isConnected}
                        isConnecting={isConnecting}
                        isUserMuted={isUserMuted}
                        onConnect={onConnect}
                        onDisconnect={onDisconnect}
                        onToggleMute={onToggleMute}
                    />
                </div>
            </div>
        </>
    );
};

export default MobileTalkOverlay;
